/** Per-feeder verification-progress card (Verification v2, item 4).
 *
 *  Renders meters + transformers verified-% bars for a feeder, plus the global
 *  unlinked work-queue counts. Prefers a caller-supplied `progress` (e.g. the
 *  Data-Quality rollup already carries `verification`) to avoid a duplicate
 *  fetch; falls back to `GET /feeders/{id}/verification` when standalone. */

import { useQuery } from '@tanstack/react-query'
import {
  getFeederVerification,
  getUnlinkedMeters,
  getUnlinkedTransformers,
} from '../../api/topology.ts'
import type {
  FeederVerificationProgress,
  VerificationSummary,
} from '../../types/dataQuality.ts'
import { friendlyError } from '../../utils/friendlyError.ts'

interface VerificationProgressCardProps {
  feederId: number
  /** When the caller already holds the rollup's verification block, pass it to
   *  skip the per-feeder fetch (the rollup and this card then share one call). */
  progress?: FeederVerificationProgress
}

const UNLINKED_LIMIT = 1000

export function VerificationProgressCard({ feederId, progress }: VerificationProgressCardProps) {
  const {
    data: fetched,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['feeder-verification', feederId],
    queryFn: () => getFeederVerification(feederId),
    enabled: progress === undefined,
    staleTime: 60_000,
  })
  const data = progress ?? fetched

  // The unlinked endpoints return ALL unlinked rows (not feeder-scoped) — this is
  // the global work-queue teaser. No count endpoint exists, so a capped list read
  // stands in; a full page reads as "N+".
  const { data: unlinkedMeters } = useQuery({
    queryKey: ['unlinked-meters'],
    queryFn: () => getUnlinkedMeters({ limit: UNLINKED_LIMIT }),
    staleTime: 60_000,
  })
  const { data: unlinkedTransformers } = useQuery({
    queryKey: ['unlinked-transformers'],
    queryFn: () => getUnlinkedTransformers({ limit: UNLINKED_LIMIT }),
    staleTime: 60_000,
  })

  if (progress === undefined && isLoading) {
    return <div className="text-sm text-gray-400">Loading verification progress...</div>
  }
  // Error state distinct from empty (spec §9): a failed request is not "no data".
  if (progress === undefined && error) {
    return (
      <div className="text-sm text-red-600">
        {friendlyError(error, 'load verification progress')} This is a request
        error, not an empty feeder.
      </div>
    )
  }
  if (!data) return null

  const meterQueue = countLabel(unlinkedMeters?.length)
  const xfmrQueue = countLabel(unlinkedTransformers?.length)

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        Feeder verification progress
      </p>
      <ProgressBar label="Meters" summary={data.meters} />
      <ProgressBar label="Transformers" summary={data.transformers} />
      {(unlinkedMeters !== undefined || unlinkedTransformers !== undefined) && (
        <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
          Unlinked work queue: {meterQueue} meter(s), {xfmrQueue} transformer(s) awaiting a link.
        </div>
      )}
    </div>
  )
}

function countLabel(len: number | undefined): string {
  if (len === undefined) return '—'
  return len >= UNLINKED_LIMIT ? `${len}+` : String(len)
}

function ProgressBar({ label, summary }: { label: string; summary: VerificationSummary }) {
  const pct = summary.verified_percentage
  return (
    <div>
      <div className="mb-0.5 flex justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className="tabular-nums text-gray-700">
          {summary.verified}/{summary.total} verified ({pct.toFixed(1)}%)
        </span>
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div style={{ width: `${Math.min(100, pct)}%`, backgroundColor: '#2ecc71' }} />
      </div>
      {(summary.flagged > 0 || summary.needs_review > 0) && (
        <div className="mt-0.5 flex gap-3 text-[10px] text-gray-500">
          {summary.flagged > 0 && <span>{summary.flagged} flagged</span>}
          {summary.needs_review > 0 && (
            <span className="text-amber-700">{summary.needs_review} needs review</span>
          )}
        </div>
      )}
    </div>
  )
}
